import icons from '../../../markons/markons'
import {ActionButtonIcon} from '../SaveSubmitButton'
import ActionButton from '../ActionButton'
import type {
    ChangeEventHandler, Dispatch, MouseEventHandler} from 'react';
import {
    useMemo, useRef, useState
} from 'react'
import css from './dt-popover.less'
import cn from 'classnames'
import CheckboxLabel from '../CheckboxLabel'
import Icon from '../Icon'
import type {SelectChangeEventHandler} from '../vanilla/Select';
import Select from '../vanilla/Select'
import RadioButton from '../RadioButton'
import RadioGroup from '../RadioGroup'
import type {AnyRowData, DataTableProps, DataTableState} from './dt-types'
import {arrayDeleteIndex, arrayReplace, arraySetChange, arraySwap, mapDefined} from '../../util/collections'
import {notifyError} from '../../notify'
// import { Popover } from 'react-tiny-popover'
import Popover from '../Popover'
import { DataTable_Length } from './dt-header'
import {BlockSpacer} from '../spacer'

// https://datatables.net/extensions/buttons/examples/column_visibility/columnText.html


export interface DtColumnConfigProps<TRow extends AnyRowData> {
    settings: DataTableProps<TRow>;
    state: DataTableState;
    mergeState: Dispatch<Partial<DataTableState>>;
}

interface DtColumnConfigPopoverProps<TRow extends AnyRowData> extends DtColumnConfigProps<TRow> {
}

function DtColumnConfigPopover<TRow extends AnyRowData>({
    settings,
    state,
    mergeState
}: DtColumnConfigPopoverProps<TRow>) {
    const orderColumns = useMemo(() => mapDefined(settings.columns, (col, n) => {
        const orderable = col.orderable ?? settings.ordering ?? true
        if(!orderable) return undefined
        return {
            value: n,
            text: col.optionLabel || col.title || `Column ${n + 1}`,
        }
    }), [settings.columns, settings.ordering])

    // console.log(state.order, orderColumns)

    const addOrder = () => {
        const currentOrderColumns = new Set(state.order.map(o => o[0]))
        const initCol = orderColumns.find(oc => !currentOrderColumns.has(oc.value))
        if(initCol === undefined) {
            notifyError("No more columns to sort by")
        } else {
            mergeState({
                order: [...state.order, [initCol.value, 'asc']]
            })
        }
    }

    return <div className={`dataTables_inputs ${css.popover}`}>
        {settings.lengthChange ? <div>
            <h4 className={css.header}>Page Size</h4>
            <DataTable_Length state={state} mergeState={mergeState} />
        </div> : null}
        <div>
            <h4 className={css.header}>Show Columns</h4>
            <ul className={css.columnList}>
                {settings.columns.map((col, n) => {
                    const checked = !state.hiddenColumns.includes(n)
                    const disabled = checked && state.hiddenColumns.length >= settings.columns.length - 1
                    const changeHandler: ChangeEventHandler<HTMLInputElement> = ev => {
                        mergeState({
                            hiddenColumns: arraySetChange(!ev.currentTarget.checked, state.hiddenColumns, n)
                        })
                    }
                    return (
                        <li key={n}>
                            <div className={css.colOptionFlex}>
                                <CheckboxLabel checked={checked} disabled={disabled}
                                    onChange={changeHandler}>{col.optionLabel || col.title || `Column ${n + 1}`}</CheckboxLabel>
                                {/*<span><Icon name={icons.ionArrowLeftB} /><Icon name={icons.ionArrowRightB} /></span>*/}
                            </div>
                        </li>
                    )
                })}
            </ul>
        </div>
        {orderColumns.length ? <div>
            <h4 className={css.header}>Sort Columns</h4>
            <ul className={css.columnList}>
                {state.order.map(([colIdx, dir], n) => {
                    const selectChange: SelectChangeEventHandler<number> = ev => {
                        const existingIdx = state.order.findIndex(o => o[0] === ev.value)
                        mergeState({
                            order: existingIdx < 0 ? arrayReplace(state.order, n, [ev.value, dir]) : arraySwap(state.order, existingIdx, n)
                        })
                    }
                    const dirChange: ChangeEventHandler<HTMLInputElement> = ev => {
                        mergeState({
                            order: arrayReplace(state.order, n, [colIdx, ev.currentTarget.value])
                        })
                    }
                    const delColumn: MouseEventHandler<HTMLButtonElement> = ev => {
                        mergeState({
                            order: arrayDeleteIndex(state.order, n)
                        })
                    }

                    return (
                        <li key={n}>
                            <div className={css.colOptionFlex}>
                                {/*<Select options={[{value: 'col1', text: "Column 1"}]} />*/}
                                <Select className={css.select}
                                    value={colIdx}
                                    options={orderColumns}
                                    onChange={selectChange} />
                                <RadioGroup value={dir} onChange={dirChange} white>
                                    <RadioButton value="asc" icon={icons.faSortAlphaUp}>Asc</RadioButton>
                                    <RadioButton value="desc" icon={icons.faSortAlphaDownAlt}>Desc</RadioButton>
                                </RadioGroup>
                                <ActionButtonIcon icon={icons.ionTrashA} onClick={delColumn} />
                            </div>
                        </li>
                    )
                })}
            </ul>
            <BlockSpacer height={2}/>
            <ActionButtonIcon icon={icons.miscPlus}
                disabled={state.order.length >= orderColumns.length}
                onClick={addOrder}>Add</ActionButtonIcon>
        </div> : null}
    </div>
}



export function DtColumnConfig<TRow extends AnyRowData>(props: DtColumnConfigProps<TRow>) {

    const columnCount = props.settings.columns.length
    const visColumnCount = columnCount - props.state.hiddenColumns.length

    const [open, setOpen] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)

    return <>
        <ActionButton
            ref={buttonRef}
            className={cn('dataTables_header_button', css.button)}
            onMouseDown={ev => {
                // ev.buttonClicked = true
                // console.log('button clicked',ev.buttonClicked,open)
                // console.log('open!')
                if(!open) {
                    ev.stopPropagation()
                    setOpen(true)
                }
            }}
        >
            <span>Options</span>
            {/*{visColumnCount < columnCount ? <span className="hidden-small">({visColumnCount}/{columnCount})</span> : null}*/}
            <Icon name={icons.ionArrowDownB} />
        </ActionButton>
        <Popover open={open} onClose={() => {
            // console.log('closing!')
            setOpen(false)
        }} anchor={buttonRef.current}>
            <DtColumnConfigPopover {...props} />
        </Popover>
    </>
}
